import {
  Stack,
  Text,
  Group,
  SegmentedControl,
  SegmentedControlItem,
  rem,
} from '@mantine/core';
import cn from 'clsx';
import { NumberInput } from 'src/components/NumberInput/NumberInput';

const SLIPPAGE_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: '0.1%', value: '0.1' },
  { label: '0.5%', value: '0.5' },
  { label: 'Custom', value: 'custom' },
];

type SwapSettingsProps = {
  slippageValue: string;
  customSlippageValue: string | undefined;
  compactSlippageValues?: boolean;
  deadlineValue: string | undefined;
  onSlippageChange: (value: string) => void;
  onCustomSlippageChange: (value: string | undefined) => void;
  onDeadlineChange: (value: string | undefined) => void;
};

export function SwapSettings({
  slippageValue,
  customSlippageValue,
  compactSlippageValues = true,
  deadlineValue,
  onSlippageChange,
  onCustomSlippageChange,
  onDeadlineChange,
}: SwapSettingsProps) {
  return (
    <Stack gap={24}>
      <Stack gap={8}>
        <Group justify="space-between">
          <Text fz={14} fw={500} c="#7E7E95">
            Max. Slippage
          </Text>
        </Group>

        <Group
          gap={8}
          wrap="nowrap"
          className="p-1 rounded-[16px] border border-solid border-[#202027]"
        >
          <SegmentedControl
            data={SLIPPAGE_OPTIONS.slice(0, -1)}
            value={slippageValue}
            withItemsBorders={false}
            styles={{
              root: {
                width: 'calc(75% - 16px)',
                outline: 'none',
                background: 'transparent',
                gap: rem(8),
                height: rem(36),
                overflow: 'visible',
                flexShrink: 0,
              },
              indicator: { display: 'none' },
            }}
            classNames={{
              label: cn(
                'px-4 rounded-[12px] !leading-[36px] text-xs',
                'bg-transparent text-[#3E3E4C] !font-[600] transition-colors',
                'data-[active]:text-primarybrand data-[active]:bg-secondarybrand/10'
              ),
            }}
            onChange={onSlippageChange}
          />

          <NumberInput
            value={customSlippageValue}
            autoFocus={slippageValue === 'custom'}
            inputProps={{
              h: 36,
              classNames: {
                wrapper: 'h-[36px]',
                input:
                  'h-[36px] min-h-[36px] border-transparent focus:border-primarybrand bg-[#151519] rounded-[12px] text-xs',
              },
            }}
            rightSection="%"
            onChange={(newValue) => {
              onCustomSlippageChange(newValue);

              if (newValue) {
                onSlippageChange('custom');
              } else {
                onSlippageChange('auto');
              }
            }}
          />
        </Group>
      </Stack>

      <Group justify="space-between" wrap="nowrap">
        <Text c="#7E7E95">Transaction Deadline</Text>
        <NumberInput
          value={deadlineValue}
          inputProps={{
            min: 1,
            max: 1440, // 24 hours
            step: 1,
            w: 100,
            h: 36,
            classNames: {
              wrapper: 'h-[36px]',
              input:
                '!px-3 h-[36px] min-h-[36px] border-transparent focus:border-[#202027] bg-[#151519] rounded-[12px] text-sm',
            },
          }}
          rightSection="min"
          rightSectionWidth={60}
          rightSectionProps={{ className: 'text-[#7E7E95] text-sm font-[500]' }}
          onChange={onDeadlineChange}
        />
      </Group>
    </Stack>
  );
}
